import { GlobalConfigService } from '../../../core/services';
// Deep import: the reporting barrel's graph loops back into the layout shell (module cycle).
import { REPORT_REGISTRY, ReportKey } from '../../../domains/reporting/reports/report.registry';
import { SidebarItemDefinition } from './sidebar-item.model';

const temporaryPlaceholderKeywords = ['test', 'sidebar-redesign', 'remove'];

// TODO(sidebar-redesign): Replace placeholders with real routes as each screen is implemented.
function getPlaceholder(label: string, keywords: string[]): SidebarItemDefinition {
    return {
        label,
        routerLink: '/test',
        securityRuleKey: 'sysAdmin',
        isPlaceholder: true,
        keywords: [...temporaryPlaceholderKeywords, ...keywords]
    };
}

export function getSidebarItems(configService: GlobalConfigService): SidebarItemDefinition[] {
    const legacyDashboardsItem: SidebarItemDefinition = {
        label: 'Dashboards',
        section: 'home',
        icon: 'speed',
        url: configService.legacyAppUrl,
        isLegacyDashboardLink: true,
        keywords: ['legacy', 'home']
    };
    const nextGenDashboardsItem: SidebarItemDefinition = { label: 'Dashboards (NG)', section: 'home', icon: 'dashboard', routerLink: '/dashboards', securityRuleKey: 'sysAdmin', keywords: ['home'] };
    const businessDomainItems: SidebarItemDefinition[] = [
        {
            label: 'ADEPT',
            iconImageSrc: '/adept-icon-sm.png',
            items: [
                {
                    label: 'Reports',
                    items: [
                        getPlaceholder('ADEPT Performance Analysis', ['report', 'adept', 'performance']),
                        getPlaceholder('Run History', ['report', 'adept', 'run', 'history']),
                        getPlaceholder('Dynamic Reserve', ['report', 'adept', 'dynamic reserve'])
                    ]
                },
                {
                    label: 'Setup',
                    items: [getPlaceholder('ADEPT Groups', ['maintenance', 'adept', 'group']), getPlaceholder('Optimization Settings', ['maintenance', 'adept', 'optimization', 'setting'])]
                }
            ]
        },
        {
            label: 'Alerts',
            icon: 'notifications',
            items: [
                { label: 'Alert Catalog', routerLink: '/alerts/alert-catalog', securityRuleKey: 'sysAdmin', keywords: ['admin', 'sysadmin', 'alert', 'catalog'] },
                getPlaceholder('Alert Results', ['report', 'alert', 'result']),
                {
                    label: 'Reports',
                    items: [getPlaceholder('Exception Results', ['report', 'alert', 'exception'])]
                },
                {
                    label: 'Setup',
                    items: [getPlaceholder('Alert Rules', ['maintenance', 'alert', 'rule']), getPlaceholder('Notification Settings', ['maintenance', 'alert', 'notification', 'setting'])]
                }
            ]
        },
        {
            label: 'Costing',
            icon: 'paid',
            items: [
                {
                    label: 'Reports',
                    items: [getPlaceholder('Costing Analysis', ['report', 'costing', 'cost']), getPlaceholder('Cost Overrides', ['report', 'costing', 'override'])]
                },
                {
                    label: 'Setup',
                    items: [
                        getPlaceholder('Cost Settings', ['maintenance', 'costing', 'setting']),
                        getPlaceholder('Cost Categories', ['maintenance', 'costing', 'category']),
                        getPlaceholder('Cost Category Mapping', ['maintenance', 'costing', 'category', 'mapping']),
                        getPlaceholder('Cost Differentials', ['maintenance', 'costing', 'differential']),
                        getPlaceholder('Cost Overrides', ['maintenance', 'costing', 'override'])
                    ]
                }
            ]
        },
        {
            label: 'Customers',
            icon: 'groups',
            items: [
                getPlaceholder('Customer Insights', ['customer', 'insights', 'profile', 'tabs']),
                getPlaceholder('Customer Search', ['maintenance', 'customer', 'search']),
                {
                    label: 'Reports',
                    items: [
                        getPlaceholder('Customer Profitability', ['report', 'customer', 'profitability']),
                        getPlaceholder('Customer Gains', ['report', 'customer', 'gain']),
                        getPlaceholder('Customer Gains and Losses', ['report', 'customer', 'gain', 'loss'])
                    ]
                },
                {
                    label: 'Setup',
                    items: [getPlaceholder('Contacts', ['maintenance', 'customer', 'contact']), getPlaceholder('Addresses', ['maintenance', 'customer', 'address']), getPlaceholder('Consumer Users', ['maintenance', 'customer', 'consumer user'])]
                }
            ]
        },
        {
            label: 'Delivery',
            icon: 'local_shipping',
            items: [
                {
                    label: 'Reports',
                    items: [
                        { label: 'Delivery Report', routerLink: '/reports/delivery', securityRuleKey: REPORT_REGISTRY[ReportKey.Delivery].securityRuleKey, keywords: ['report', 'delivery', 'deliveries'] },
                        getPlaceholder('Delivery Performance', ['report', 'delivery', 'performance']),
                        getPlaceholder('Delivery History', ['report', 'delivery', 'history']),
                        getPlaceholder('Batch Deliveries', ['report', 'delivery', 'batch', 'deliveries']),
                        getPlaceholder('Delivery Efficiency', ['report', 'delivery', 'efficiency']),
                        getPlaceholder('Delivery Forecasting', ['report', 'delivery', 'forecasting']),
                        getPlaceholder('Driver Performance', ['report', 'delivery', 'driver', 'performance']),
                        getPlaceholder('Average Gallons Delivery History', ['report', 'delivery', 'average', 'gallon', 'history'])
                    ]
                },
                {
                    label: 'Setup',
                    items: [
                        getPlaceholder('Delivery Types', ['maintenance', 'delivery type']),
                        getPlaceholder('Source Delivery Types', ['maintenance', 'source', 'delivery type', 'bos']),
                        getPlaceholder('Driver Exclusion', ['maintenance', 'driver', 'exclusion']),
                        getPlaceholder('Driver Mapping', ['maintenance', 'driver', 'mapping', 'source', 'bos']),
                        getPlaceholder('Delivery Schedules', ['maintenance', 'schedule'])
                    ]
                }
            ]
        },
        {
            label: 'Finance',
            icon: 'payments',
            items: [
                {
                    label: 'Reports',
                    items: [
                        { label: 'Hedge Coverage', routerLink: '/reports/hedge-coverage', securityRuleKey: REPORT_REGISTRY[ReportKey.HedgeCoverage].securityRuleKey, keywords: ['report', 'hedge', 'coverage', 'finance'] },
                        getPlaceholder('Accounts Receivable', ['report', 'finance', 'accounts receivable']),
                        getPlaceholder('AR Summary FuelPak', ['report', 'finance', 'accounts receivable', 'fuelpak']),
                        getPlaceholder('Sales Report', ['report', 'finance', 'sales']),
                        getPlaceholder('Margin Analysis', ['report', 'finance', 'margin'])
                    ]
                },
                {
                    label: 'Setup',
                    items: [
                        getPlaceholder('Posting Codes', ['maintenance', 'finance', 'posting code']),
                        getPlaceholder('Category Codes', ['maintenance', 'finance', 'category code']),
                        getPlaceholder('Finance Standards', ['maintenance', 'finance', 'standard']),
                        getPlaceholder('Hedge Positions', ['maintenance', 'finance', 'hedge', 'position'])
                    ]
                }
            ]
        },
        {
            label: 'Inventory',
            icon: 'inventory',
            items: [
                {
                    label: 'Reports',
                    items: [
                        {
                            label: 'Inventory Transaction',
                            routerLink: '/reports/inventory-transaction',
                            securityRuleKey: REPORT_REGISTRY[ReportKey.InventoryTransaction].securityRuleKey,
                            keywords: ['report', 'inventory', 'transaction', 'transactions']
                        },
                        getPlaceholder('Liquid Fuels', ['report', 'inventory', 'liquid fuel']),
                        getPlaceholder('Inventory Reconciliation', ['report', 'inventory', 'reconciliation'])
                    ]
                },
                {
                    label: 'Setup',
                    items: [getPlaceholder('Bulk Plants', ['maintenance', 'inventory', 'bulk plant']), getPlaceholder('Inventory Settings', ['maintenance', 'inventory', 'setting'])]
                }
            ]
        },
        {
            // Oil-delivery products (heating oil, propane) are the business — barrel beats the generic category icon.
            label: 'Products',
            icon: 'oil_barrel',
            keywords: ['oil', 'propane', 'fuel'],
            items: [
                {
                    label: 'Reports',
                    items: [getPlaceholder('Product Performance', ['report', 'product', 'performance']), getPlaceholder('Product Categories', ['report', 'product', 'category'])]
                },
                {
                    label: 'Setup',
                    items: [
                        getPlaceholder('Source Products', ['maintenance', 'source', 'product', 'bos']),
                        getPlaceholder('Products', ['maintenance', 'product', 'mapping']),
                        getPlaceholder('Report Product Categories', ['maintenance', 'product', 'category', 'report']),
                        getPlaceholder('Budget Product Categories', ['maintenance', 'product', 'category', 'budget'])
                    ]
                }
            ]
        },
        {
            label: 'Service',
            icon: 'home_repair_service',
            items: [
                {
                    label: 'Reports',
                    items: [
                        getPlaceholder('Service Calls', ['report', 'service', 'call']),
                        getPlaceholder('Service History', ['report', 'service', 'history']),
                        getPlaceholder('Service Revenue', ['report', 'service', 'revenue']),
                        getPlaceholder('Service Rework', ['report', 'service', 'rework']),
                        getPlaceholder('Callback Analysis', ['report', 'service', 'callback']),
                        getPlaceholder('Excess Calls', ['report', 'service', 'excess call'])
                    ]
                },
                {
                    label: 'Setup',
                    items: [
                        getPlaceholder('Call Reasons', ['maintenance', 'service', 'call reason']),
                        getPlaceholder('Technicians', ['maintenance', 'service', 'technician']),
                        getPlaceholder('Service Contract Types', ['maintenance', 'service', 'contract type'])
                    ]
                }
            ]
        },
        {
            label: 'Tank Monitoring',
            icon: 'sensors',
            keywords: ['ams', 'tms'],
            items: [
                { label: 'Tank Monitors', routerLink: '/tank-monitors', securityRuleKey: 'sysAdmin', keywords: ['maintenance', 'ams', 'tms', 'monitor'] },
                {
                    label: 'Reports',
                    items: [
                        getPlaceholder('TMS Delivery Efficiency', ['report', 'ams', 'tms', 'delivery', 'efficiency']),
                        getPlaceholder('TMS Exceptions', ['report', 'ams', 'tms', 'exception']),
                        getPlaceholder('Monitor Compliance', ['report', 'ams', 'tms', 'monitor', 'compliance']),
                        getPlaceholder('Weekly Queries', ['report', 'ams', 'tms', 'weekly']),
                        getPlaceholder('Daily Levels', ['report', 'ams', 'tms', 'level']),
                        getPlaceholder('Install Summary', ['report', 'ams', 'tms', 'install'])
                    ]
                },
                {
                    label: 'Setup',
                    items: [
                        getPlaceholder('Monitor Inventory', ['maintenance', 'ams', 'tms', 'monitor', 'inventory']),
                        getPlaceholder('TMS Contacts', ['maintenance', 'ams', 'tms', 'contact']),
                        // Paygo is a retired offering possibly still used by Tank Monitoring billing — evaluate rename/removal before these convert to real routes.
                        getPlaceholder('Paygo Products', ['maintenance', 'ams', 'paygo', 'product']),
                        getPlaceholder('Paygo Adders', ['maintenance', 'ams', 'paygo', 'adder']),
                        getPlaceholder('Billing Cycle', ['maintenance', 'ams', 'paygo', 'billing cycle']),
                        getPlaceholder('Exception Settings', ['maintenance', 'ams', 'tms', 'exception', 'setting'])
                    ]
                }
            ]
        },
        {
            label: 'Tanks',
            icon: 'propane_tank',
            items: [
                {
                    label: 'Reports',
                    items: [
                        getPlaceholder('Tank Profitability', ['report', 'tank', 'profitability']),
                        getPlaceholder('Tank Utilization', ['report', 'tank', 'utilization']),
                        getPlaceholder('Average Gallons by Tank Size', ['report', 'tank', 'average gallon'])
                    ]
                },
                {
                    label: 'Setup',
                    items: [getPlaceholder('Tank Assets', ['maintenance', 'tank', 'asset']), getPlaceholder('Tank Groups', ['maintenance', 'tank', 'group']), getPlaceholder('Container Types', ['maintenance', 'tank', 'container type'])]
                }
            ]
        }
    ];

    // Flat list (no subject captions): too few items per group to earn a heading — see
    // caption-threshold rule in .claude/skills/sidebar-keywords. Ordering keeps the old group adjacency.
    const administrationItem: SidebarItemDefinition = {
        label: 'Administration',
        section: 'system',
        icon: 'admin_panel_settings',
        items: [
            { label: 'Users', routerLink: '/users', securityRuleKey: 'sysAdmin', keywords: ['maintenance', 'admin', 'user', 'manage'] },
            getPlaceholder('Security Groups', ['maintenance', 'admin', 'security', 'group']),
            { label: 'Dashboards', routerLink: '/dashboards/admin', securityRuleKey: 'sysAdmin', keywords: ['maintenance', 'admin', 'dashboard', 'template', 'user', 'copy'] },
            getPlaceholder('Default Dashboards', ['maintenance', 'admin', 'dashboard', 'default']),
            getPlaceholder('Widget Settings', ['maintenance', 'admin', 'setting', 'widget', 'dashboard']),
            getPlaceholder('Hierarchy', ['maintenance', 'admin', 'hierarchy']),
            getPlaceholder('Hierarchy Levels', ['maintenance', 'admin', 'hierarchy', 'level'])
        ]
    };

    // Workspace pages sit caption-free above the SETUP group, mirroring the domain pattern.
    const sysAdminItem: SidebarItemDefinition = {
        label: 'SysAdmin',
        section: 'system',
        icon: 'shield_person',
        trailingBadge: 'INTERNAL',
        items: [
            { label: 'Report Catalog', routerLink: '/reports/report-catalog', securityRuleKey: 'sysAdmin', keywords: ['admin', 'sysadmin', 'report', 'reports', 'catalog', 'tool'] },
            { label: 'Security Audit', routerLink: '/users/security-audit', securityRuleKey: 'sysAdmin', keywords: ['admin', 'sysadmin', 'permission', 'security', 'audit', 'tool'] },
            { label: 'Sysadmin Test', routerLink: '/test', securityRuleKey: 'sysAdmin', isEnabled: false, keywords: ['admin', 'sysadmin', 'developer', 'test'] },
            {
                label: 'Setup',
                items: [
                    { label: 'Subscribers', routerLink: '/admin/subscribers', securityRuleKey: 'sysAdmin', keywords: ['maintenance', 'admin', 'sysadmin', 'subscriber', 'account'] },
                    { label: 'Source Systems', routerLink: '/admin/source-systems', securityRuleKey: 'sysAdmin', keywords: ['maintenance', 'admin', 'sysadmin', 'source', 'system', 'bos'] },
                    getPlaceholder('Connection Setup', ['maintenance', 'admin', 'sysadmin', 'connection', 'source system']),
                    getPlaceholder('Subscriber Registration', ['maintenance', 'admin', 'sysadmin', 'registration']),
                    getPlaceholder('ETL Status', ['maintenance', 'admin', 'sysadmin', 'etl'])
                ]
            }
        ]
    };

    // Keep home, business, and system items in the same order as the sidebar.
    return [legacyDashboardsItem, nextGenDashboardsItem, ...businessDomainItems, administrationItem, sysAdminItem];
}
